import React, { useState, useEffect, useRef } from 'react';
import { businessService, categoryService, productService } from '../services/api';
import './PrintMenu.css';

interface Product {
  id: number;
  name: string;
  description?: string;
  price: number;
  price_unit?: string;
  category_id: number;
  allergens: Array<{ id: number; name: string }>;
  ingredients: Array<{ id: number; name: string }>;
}

interface Category {
  id: number;
  name: string;
  description?: string;
  children?: Category[];
  sort_order?: number;
}

interface CategorySection {
  category: Category;
  subcategories: Array<{
    subcat: Category | null;
    products: Product[];
  }>;
}

// Altezza utile contenuto A4 (px a 96dpi): 297mm ≈ 1123px, meno 56+48px di padding
const A4_CONTENT_PX = 1019;
// Spazio occupato dall'intestazione (nome locale + ornamento) nella prima pagina
const HEADER_PX = 190;
// Margine tra sezioni
const SECTION_GAP_PX = 32;

const PrintMenu: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [businessInfo, setBusinessInfo] = useState<any>(null);
  const [sections, setSections] = useState<CategorySection[]>([]);
  // null = ancora in misurazione, array = pagine calcolate
  const [pages, setPages] = useState<CategorySection[][] | null>(null);
  const measureRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [generatedDate] = useState(() =>
    new Date().toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' })
  );

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [bizResponse, categoriesResponse] = await Promise.all([
          businessService.get(),
          categoryService.getPublic(),
        ]);

        setBusinessInfo(bizResponse);

        const cats: Category[] = categoriesResponse?.data?.categories || [];
        cats.sort((a, b) => {
          const ao = a.sort_order ?? 0;
          const bo = b.sort_order ?? 0;
          if (ao !== bo) return ao - bo;
          return (a.name || '').localeCompare(b.name || '');
        });

        const sectionsData: CategorySection[] = [];

        for (const cat of cats) {
          const productsResponse = await productService.getByCategory(cat.id);
          const products: Product[] = productsResponse?.data?.products || [];

          const children = (cat.children || []).slice().sort((a, b) => {
            const ao = a.sort_order ?? 0;
            const bo = b.sort_order ?? 0;
            if (ao !== bo) return ao - bo;
            return (a.name || '').localeCompare(b.name || '');
          });

          if (children.length > 0) {
            const subcatMap = new Map<number | null, Product[]>();
            subcatMap.set(null, []);
            for (const sub of children) subcatMap.set(sub.id, []);
            for (const p of products) {
              const subId = children.some((s) => s.id === p.category_id) ? p.category_id : null;
              const bucket = subcatMap.get(subId);
              if (bucket) bucket.push(p);
            }

            const subcategories: CategorySection['subcategories'] = children
              .map((sub) => ({ subcat: sub, products: subcatMap.get(sub.id) || [] }))
              .filter((s) => s.products.length > 0);

            const rootProducts = subcatMap.get(null) || [];
            if (rootProducts.length > 0) subcategories.unshift({ subcat: null, products: rootProducts });

            if (subcategories.length > 0) sectionsData.push({ category: cat, subcategories });
          } else if (products.length > 0) {
            sectionsData.push({ category: cat, subcategories: [{ subcat: null, products }] });
          }
        }

        setSections(sectionsData);
      } catch (err) {
        console.error('Errore nel caricamento del menu:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Dopo che le sezioni sono renderizzate nel div di misurazione, calcola le pagine
  useEffect(() => {
    if (loading || sections.length === 0) return;

    // Aspetta che il DOM abbia renderizzato il div di misurazione
    const timer = setTimeout(() => {
      const grouped: CategorySection[][] = [];
      let currentPage: CategorySection[] = [];
      let usedPx = 0;

      sections.forEach((section, i) => {
        const el = measureRefs.current[i];
        const sectionH = (el?.offsetHeight ?? 200) + SECTION_GAP_PX;

        // Budget disponibile: prima pagina ha meno spazio (c'è l'intestazione)
        const budget = grouped.length === 0
          ? A4_CONTENT_PX - HEADER_PX
          : A4_CONTENT_PX;

        if (currentPage.length === 0) {
          currentPage.push(section);
          usedPx = sectionH;
        } else if (usedPx + sectionH <= budget) {
          currentPage.push(section);
          usedPx += sectionH;
        } else {
          grouped.push([...currentPage]);
          currentPage = [section];
          usedPx = sectionH;
        }
      });

      if (currentPage.length > 0) grouped.push([...currentPage]);
      setPages(grouped);
    }, 80);

    return () => clearTimeout(timer);
  }, [sections, loading]);

  const renderSectionBody = (section: CategorySection) => (
    <>
      <h2 className="print-category-title">{section.category.name}</h2>
      {section.category.description && (
        <p className="print-category-description">{section.category.description}</p>
      )}
      {section.subcategories.map(({ subcat, products }) => (
        <div key={subcat?.id ?? 'root'} className="print-subcategory">
          {subcat && <h3 className="print-subcategory-title">{subcat.name}</h3>}
          <ul className="print-product-list">
            {products.map((product) => (
              <li key={product.id} className="print-product-item">
                <div className="print-product-row">
                  <span className="print-product-name">{product.name}</span>
                  <span className="print-product-dots" aria-hidden="true" />
                  <span className="print-product-price">
                    € {product.price.toFixed(2)}
                    {product.price_unit ? ` / ${product.price_unit}` : ''}
                  </span>
                </div>
                {product.description && (
                  <p className="print-product-description">{product.description}</p>
                )}
                {product.ingredients && product.ingredients.length > 0 && (
                  <p className="print-product-meta">
                    <em>Ingredienti:</em>{' '}
                    {product.ingredients
                      .map((i) => i.name)
                      .sort((a, b) => a.localeCompare(b, 'it'))
                      .join(', ')}
                  </p>
                )}
                {product.allergens && product.allergens.length > 0 && (
                  <p className="print-product-meta print-product-allergens">
                    <em>Allergeni:</em>{' '}
                    {product.allergens
                      .map((a) => a.name)
                      .sort((a, b) => a.localeCompare(b, 'it'))
                      .join(', ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );

  // Fase di caricamento dati o misurazione DOM
  if (loading || !pages) {
    return (
      <div className="print-menu-loading">
        <div className="print-menu-loading-spinner" />
        <p>{loading ? 'Caricamento menu…' : 'Organizzazione pagine…'}</p>

        {/* Div di misurazione — invisibile, fuori schermo */}
        {!loading && (
          <div className="print-measure-container">
            {sections.map((section, i) => (
              <div
                key={section.category.id}
                ref={(el) => { measureRefs.current[i] = el; }}
                className="print-section"
              >
                {renderSectionBody(section)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="print-menu-wrapper">
      {/* Barra azioni — nascosta in stampa */}
      <div className="print-menu-actions no-print">
        <button onClick={() => window.history.back()} className="print-back-btn">
          ← Torna alla dashboard
        </button>
        <button onClick={() => window.print()} className="print-trigger-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 6 2 18 2 18 9"/>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <rect x="6" y="14" width="12" height="8"/>
          </svg>
          Stampa / Scarica PDF
        </button>
      </div>

      {/* Indicatore pagine */}
      <p className="print-page-count no-print">
        {pages.length} {pages.length === 1 ? 'pagina' : 'pagine'}
      </p>

      {/* Pagine A4 */}
      {pages.map((pageSections, pageIdx) => (
        <div
          key={pageIdx}
          className={`print-menu-page${pageIdx < pages.length - 1 ? ' print-page-break' : ''}`}
        >
          {/* Intestazione solo nella prima pagina */}
          {pageIdx === 0 && (
            <header className="print-header">
              <p className="print-header-label">MENÙ</p>
              <h1 className="print-business-name">{businessInfo?.name || 'Il Nostro Menu'}</h1>
              {businessInfo?.description && (
                <p className="print-business-description">{businessInfo.description}</p>
              )}
              <div className="print-ornament">— ✦ —</div>
            </header>
          )}

          {/* Categorie di questa pagina */}
          {pageSections.map((section) => (
            <section key={section.category.id} className="print-section">
              {renderSectionBody(section)}
            </section>
          ))}

          {/* Numero pagina e piè di pagina nell'ultima pagina */}
          {pageIdx === pages.length - 1 ? (
            <footer className="print-footer">
              <p className="print-allergen-notice">
                Gentile cliente, in caso di allergie o intolleranze alimentari La preghiamo di
                comunicarlo sempre al personale di sala prima di ordinare. I nostri piatti possono
                contenere tracce di allergeni non indicati. Le informazioni complete sono a
                disposizione: chieda al cameriere.
              </p>
              {businessInfo?.phone && <p>Tel: {businessInfo.phone}</p>}
              {businessInfo?.website && <p>{businessInfo.website}</p>}
              <p className="print-footer-date">Menu aggiornato al {generatedDate}</p>
              <div className="print-ornament">— ✦ —</div>
            </footer>
          ) : (
            <div className="print-page-number no-print">
              Pagina {pageIdx + 1} di {pages.length}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PrintMenu;
